
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type ConsentState = {
  analytics: boolean;
  marketing: boolean;
  functionality: boolean;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const defaultConsent: ConsentState = {
  analytics: false,
  marketing: false,
  functionality: true,
};

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentState>(defaultConsent);

  // Initialise GTM consent mode and load any saved preferences
  useEffect(() => {
    // Initialise dataLayer and gtag if they don't exist yet
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // Default consent – deny analytics & marketing before GTM loads tags
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });

    // Load saved consent, if any
    const saved = localStorage.getItem('cookie_consent');
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ConsentState;
        setConsent(parsed);
        updateGoogleConsent(parsed);
      } catch (e) {
        console.error('Failed to parse saved cookie consent:', e);
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, []);

  // Update GTM consent based on the current state
  const updateGoogleConsent = (c: ConsentState) => {
    try {
      window.gtag('consent', 'update', {
        ad_storage: c.marketing ? 'granted' : 'denied',
        ad_user_data: c.marketing ? 'granted' : 'denied',
        ad_personalization: c.marketing ? 'granted' : 'denied',
        analytics_storage: c.analytics ? 'granted' : 'denied',
        functionality_storage: 'granted',
        personalization_storage: c.marketing ? 'granted' : 'denied',
      });
    } catch (e) {
      console.error('Error updating GTM consent:', e);
    }
  };

  // Persist consent to localStorage and inform GTM
  const saveConsent = (c: ConsentState) => {
    try {
      localStorage.setItem('cookie_consent', JSON.stringify(c));
      localStorage.setItem('cookie_consent_date', new Date().toISOString());
      updateGoogleConsent(c);
      setIsVisible(false);
    } catch (e) {
      console.error('Failed to save cookie consent:', e);
    }
  };

  const acceptAll = () => {
    const all: ConsentState = { analytics: true, marketing: true, functionality: true };
    setConsent(all);
    saveConsent(all);
  };

  const rejectAll = () => {
    const none: ConsentState = { analytics: false, marketing: false, functionality: true };
    setConsent(none);
    saveConsent(none);
  };

  const savePreferences = () => {
    saveConsent(consent);
  };

  const toggleConsent = (key: keyof ConsentState) => {
    if (key === 'functionality') return;
    setConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Simple bottom banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slideUp">
        <div className="bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            {/* Main banner */}
            {!showDetails && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-shield-check-line text-orange-500 text-sm"></i>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Nous utilisons des cookies pour analyser le trafic et améliorer votre expérience.{' '}
                    <Link
                      to="/politique-de-confidentialite"
                      className="text-orange-500 hover:underline cursor-pointer"
                    >
                      En savoir plus
                    </Link>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-4 py-2.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Personnaliser
                  </button>
                  <button
                    onClick={rejectAll}
                    className="px-5 py-2.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Tout refuser
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-5 py-2.5 text-xs font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-lg transition-all shadow-sm cursor-pointer whitespace-nowrap"
                  >
                    Tout accepter
                  </button>
                </div>
              </div>
            )}

            {/* Detail panel */}
            {showDetails && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900">Paramètres des cookies</h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  {/* Fonctionnels */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-xs font-semibold text-gray-900">Fonctionnels</span>
                      <span className="ml-2 text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">
                        Requis
                      </span>
                    </div>
                    <button
                      disabled
                      className="relative w-10 h-5 rounded-full bg-orange-500 cursor-not-allowed opacity-70"
                    >
                      <span className="absolute top-0.5 translate-x-5 w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>

                  {/* Analytiques */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs font-semibold text-gray-900">Analytiques</span>
                    <button
                      onClick={() => toggleConsent('analytics')}
                      className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer ${
                        consent.analytics ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                          consent.analytics ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs font-semibold text-gray-900">Marketing</span>
                    <button
                      onClick={() => toggleConsent('marketing')}
                      className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer ${
                        consent.marketing ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                          consent.marketing ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={rejectAll}
                    className="px-5 py-2.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Tout refuser
                  </button>
                  <button
                    onClick={savePreferences}
                    className="px-5 py-2.5 text-xs font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-lg transition-all shadow-sm cursor-pointer whitespace-nowrap"
                  >
                    Enregistrer mes choix
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Simple slide‑up animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
      `}</style>
    </>
  );
}
